import { useId } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon } from "@/components/icons";
import { type CustomerMembership, membershipKey } from "@/hooks/use-customer-context";
import { useCustomerStore } from "@/store";

interface AccountSwitcherProps {
  memberships: CustomerMembership[];
  active: CustomerMembership;
}

/** The account id a switcher key selects, or null when the key names no live membership. */
export function resolveSwitchTarget(memberships: CustomerMembership[], key: string): string | null {
  return memberships.find((m) => membershipKey(m) === key)?.account_id ?? null;
}

/**
 * Switcher option text. A native <option> renders one line, so the account
 * number printed on the utility's letters sits between the account name and
 * the utility name; an unnumbered account omits it.
 */
export function switcherLabel(m: CustomerMembership): string {
  return [m.name, m.account_number, m.org_name].filter((p): p is string => !!p).join(" — ");
}

/**
 * The active account as a chip, for a person with more than one account. When
 * the chip is squeezed its name and number truncate and the chevron keeps its
 * place inside the chip. A native select lies transparent over the whole
 * chip, so the platform's own picker opens and the select's focus ring circles
 * the chip; the chip's printed copy is hidden from assistive tech, where the
 * select speaks.
 * Switching the account changes the server scope: every list read keys on
 * `account_id`, so the pages refetch for the chosen account.
 */
export function AccountSwitcher({ memberships, active }: AccountSwitcherProps) {
  const { t } = useTranslation("customer");
  const setActiveAccount = useCustomerStore((s) => s.setActiveAccount);
  const id = useId();
  if (memberships.length < 2) return null;

  const handleSwitch = (key: string) => {
    const accountId = resolveSwitchTarget(memberships, key);
    if (accountId) setActiveAccount(accountId);
  };

  return (
    <div className="relative inline-flex min-h-11 max-w-full items-center gap-2 rounded-[8px] border border-line-strong bg-surface px-3 text-xs font-medium text-body">
      <span aria-hidden="true" className="flex min-w-0 items-center gap-2">
        <span className="truncate text-ink">{active.name}</span>
        {active.account_number && (
          <span className="truncate text-muted">
            {t("switcher.accountNumber", { number: active.account_number })}
          </span>
        )}
      </span>
      <ChevronDownIcon className="size-4 shrink-0 text-muted" />
      <label htmlFor={id} className="sr-only">
        {t("switcher.label")}
      </label>
      <select
        id={id}
        value={membershipKey(active)}
        onChange={(event) => handleSwitch(event.target.value)}
        className="absolute -inset-px cursor-pointer appearance-none rounded-[8px] bg-transparent text-[16px] text-transparent [&>option]:bg-surface [&>option]:text-ink"
      >
        {memberships.map((m) => (
          <option key={membershipKey(m)} value={membershipKey(m)}>
            {switcherLabel(m)}
          </option>
        ))}
      </select>
    </div>
  );
}
